/**
 * Detail mode panel content — Driver tab with the LIVE VIDEO in the
 * media slot.
 */

import React from 'react';

import { isLiveNow } from '../../hooks/useEventLiveDevices';
import DriverTab from './DriverTab';
import FeedPlayer from './FeedPlayer';
import { useNowTick } from './lag';
// type-only: avoids a runtime circular import (router renders the modes)
import type { ViewerController } from './ViewerModeRouter';

export const DetailMode: React.FC<{ c: ViewerController }> = ({ c }) => {
  const device = c.selected;
  const nowMs = useNowTick(1000);
  if (!device) return null; // router guards; satisfy the type system
  // lapOverride mounts an older session as VOD — live SSE speed must
  // not overlay replay footage (review).
  const isLive = !c.replay && isLiveNow(device, nowMs) && c.lapOverride == null;
  return (
    <DriverTab
      device={device}
      replay={c.replay}
      recordingStatus={c.recordingStatus}
      playbackSpeedKmh={c.playbackSpeedKmh}
      trailTrack={c.trailTrack}
      trailCoverage={c.trailCoverage}
      playheadUtc={c.playheadUtc}
      onSeek={c.handleSeek}
      onClose={c.goBrowse}
      videoStartMs={c.videoStartMs}
      media={
        /* The `key` prop forces a full player
           remount on session switch. Without it the instance is reused
           across A→B (camera_id is the constant phone-rear), so per-
           session refs — PDT anchors, duration ratchet, loadedCount,
           hasInitialSync, tier key — survive and corrupt B's UTC mapping,
           seekToUtc, and stage chip against A's epoch. A lap click into a
           different session swaps the mounted session via lapOverride and
           seeds the one-shot initial seek; that older session is always
           played as VOD even on a live page. */
        <FeedPlayer
          key={c.mountedPlayerKey}
          sessionId={c.mountedSessionId}
          playerRef={c.playerRef}
          onUtcUpdate={c.handlePlayheadUtc}
          onVideoEdgeUpdate={c.onPlayerEdge}
          onPdtReady={c.handlePdtReady}
          initialSeekUtcMs={c.initialSeekUtcMs}
          entryIntent={c.entryIntent}
          vod={c.playerVod}
          onRecordingStatus={c.onRecordingStatus}
          badge={c.videoBadge}
          cornerChip={
            isLive && device.speed_mps != null
              ? `${Math.round(device.speed_mps * 3.6)} km/h`
              : null
          }
          glassRail={c.glassRail}
        />
      }
      playerLag={c.playerLagInfo}
      stageProgress={c.stageProgress}
      stageLengthM={c.stageLengthM}
      compareCapable={c.compareCapable}
      compareBusy={c.compareBusy}
      onCompareOpen={c.handleCompareOpen}
      compareRun={c.compareRun}
      compareePlayerRef={c.compareePlayerRef}
            followed={c.followedIds.has(device.device_id)}
            onToggleFollow={c.toggleFollow}
      onCompareClose={c.handleCompareClose}
      isCircuit={c.isCircuit}
      laps={c.laps}
      selectedLapIdx={c.selectedLapIdx}
      onPickLap={c.handlePickLap}
      onPickLive={c.handlePickLive}
      stageEvents={c.stageEvents}
      sessions={c.driverSessions}
      onPickSession={c.handlePickSession}
      mountedSessionId={c.mountedSessionId}
    />
  );
};

export default DetailMode;
